"use client"

import { useState } from "react"
import Link from "next/link"
import { Smartphone, Check, Loader2 } from "lucide-react"
import { analytics } from "@/lib/analytics"

// Separate capture surface for the iOS app launch list. Sits alongside the
// newsletter form so users self-select — the audience that opts in here is
// high-intent for install on launch day. POSTs to /api/email/subscribe with
// context='app_waitlist' which the Resend audience sync mirrors with that
// source tag for downstream segmentation.

type Status = "idle" | "submitting" | "success" | "error"

interface AppWaitlistFormProps {
  variant?: "footer" | "default"
}

export function AppWaitlistForm({ variant = "footer" }: AppWaitlistFormProps = {}) {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<Status>("idle")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!email || status === "submitting") return

    setStatus("submitting")
    setErrorMessage(null)

    try {
      const response = await fetch("/api/email/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, context: "app_waitlist" }),
      })

      if (!response.ok) {
        // 409 = already subscribed — treat as success at the UI layer; the
        // user is on the list either way.
        if (response.status === 409) {
          analytics.newsletterSignup(email, "app_waitlist")
          setStatus("success")
          return
        }
        throw new Error("Subscription failed")
      }

      analytics.newsletterSignup(email, "app_waitlist")
      setStatus("success")
    } catch {
      setStatus("error")
      setErrorMessage("Something went wrong. Please try again.")
    }
  }

  if (status === "success") {
    return (
      <div className="flex items-start gap-2 text-sm text-green-800">
        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <p>
          You&apos;re on the list. We&apos;ll email you the day it ships.
        </p>
      </div>
    )
  }

  const isFooter = variant === "footer"

  return (
    <div>
      <p className={`font-bold text-gray-900 mb-2 text-sm tracking-tight flex items-center gap-1.5`}>
        <Smartphone className="w-3.5 h-3.5 text-green-700" aria-hidden="true" />
        Get the iOS app
      </p>
      <p className="text-xs text-gray-600 mb-2 leading-relaxed">
        Be first to know when it launches.
      </p>
      <Link
        href="/app"
        className="mb-3 inline-flex text-xs font-semibold text-green-800 underline underline-offset-4 hover:text-green-900"
      >
        See the iOS app preview
      </Link>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email"
          required
          aria-label="Email for iOS app launch notification"
          className={`flex-1 min-w-0 px-3 py-2 text-base border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:border-transparent ${
            isFooter ? "" : ""
          }`}
        />
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex-shrink-0 px-4 py-2 bg-gray-900 hover:bg-gray-800 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition-colors min-h-[44px] inline-flex items-center justify-center gap-1.5"
        >
          {status === "submitting" ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            "Notify me"
          )}
        </button>
      </form>
      {status === "error" && errorMessage && (
        <p className="mt-2 text-xs text-red-700" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  )
}
